import { useMemo, useRef, useState } from 'react';
import Modal from '@/components/base/Modal';
import {
  RESTORE_MODULES,
  parseBackupFile,
  restoreTables,
  type ParsedBackup,
} from '@/services/restore';

type Feedback =
  | { type: 'success'; msg: string }
  | { type: 'error'; msg: string }
  | null;

const CONFIRM_WORD = 'RESTAURAR';

// Etiqueta amigable para una tabla usando el módulo al que pertenece.
function tableLabel(table: string): string {
  for (const mod of RESTORE_MODULES) {
    if (mod.tables.includes(table)) return mod.label;
  }
  return table;
}

export default function RestoreBackupSection() {
  const inputRef = useRef<HTMLInputElement>(null);

  const [backup, setBackup] = useState<ParsedBackup | null>(null);
  const [fileName, setFileName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [parseError, setParseError] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, table: '' });

  const selectedTables = useMemo(() => {
    if (!backup) return [];
    return RESTORE_MODULES.filter((m) => selected.has(m.id)).flatMap((m) => m.tables);
  }, [backup, selected]);

  const selectedRows = useMemo(() => {
    if (!backup) return 0;
    return selectedTables.reduce((sum, t) => sum + (backup.counts[t] ?? 0), 0);
  }, [backup, selectedTables]);

  function resetAll() {
    setBackup(null);
    setFileName('');
    setSelected(new Set());
    setFeedback(null);
    setParseError('');
    if (inputRef.current) inputRef.current.value = '';
  }

  function handlePickFile() {
    setParseError('');
    setFeedback(null);
    inputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError('');
    setFeedback(null);
    try {
      const parsed = await parseBackupFile(file);
      setBackup(parsed);
      setFileName(file.name);
      // Por defecto todo lo que traiga datos viene seleccionado.
      const all = new Set<string>();
      for (const mod of RESTORE_MODULES) {
        if (mod.tables.some((t) => (parsed.counts[t] ?? 0) > 0)) all.add(mod.id);
      }
      setSelected(all);
    } catch (err) {
      setBackup(null);
      setSelected(new Set());
      setParseError(err instanceof Error ? err.message : 'No se pudo leer el archivo.');
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function toggleModule(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    if (!backup) return;
    const all = new Set<string>();
    for (const mod of RESTORE_MODULES) {
      if (mod.tables.some((t) => (backup.counts[t] ?? 0) > 0)) all.add(mod.id);
    }
    setSelected(all);
  }

  function clearAll() {
    setSelected(new Set());
  }

  async function handleConfirmRestore() {
    if (!backup || confirmText.trim().toUpperCase() !== CONFIRM_WORD) return;
    setRestoring(true);
    setProgress({ done: 0, total: selectedTables.length, table: '' });
    const report = await restoreTables(backup, selectedTables, (done, total, table) => {
      setProgress({ done, total, table });
    });
    setRestoring(false);
    setConfirmOpen(false);
    setConfirmText('');

    const restoredRows = report.restored.reduce((sum, r) => sum + r.count, 0);
    if (report.error) {
      setFeedback({ type: 'error', msg: report.error });
      return;
    }
    if (report.failed.length > 0) {
      const labels = Array.from(new Set(report.failed.map((f) => tableLabel(f.table)))).join(', ');
      setFeedback({
        type: 'error',
        msg: `Restauración parcial: ${restoredRows.toLocaleString()} registro(s) recuperados, pero fallaron los módulos: ${labels}. Revisa que las tablas "padre" relacionadas también se restauraran.`,
      });
    } else {
      setFeedback({
        type: 'success',
        msg: `¡Restauración completa! ${restoredRows.toLocaleString()} registro(s) recuperados en ${report.restored.length} tabla(s).`,
      });
    }
  }

  const lastTableLabel = progress.table ? tableLabel(progress.table) : '';
  const progressPct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
          <i className="ri-upload-cloud-2-line text-primary-600"></i>
        </div>
        <div>
          <h2 className="text-sm font-semibold text-foreground-900">Restaurar respaldo</h2>
          <p className="text-xs text-foreground-500">
            Sube un archivo <code className="text-[11px]">PCP_Backup_...json</code> para recuperar tus datos. Puedes elegir qué módulos devolver.
          </p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <input
          ref={inputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleFileChange}
          className="hidden"
        />

        {!backup && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-foreground-600">
              El respaldo se fusiona con lo que ya existe (no borra nada): si un registro ya está, se
              actualiza; si no, se agrega.
            </p>
            <button
              onClick={handlePickFile}
              className="text-sm px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-folder-open-line mr-1.5"></i>Seleccionar archivo
            </button>
          </div>
        )}

        {parseError && (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <i className="ri-error-warning-line mt-0.5"></i>
            <span>{parseError}</span>
          </div>
        )}

        {feedback && (
          <div
            className={`flex items-start gap-2 text-sm rounded-lg px-4 py-3 border ${
              feedback.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`}
          >
            <i
              className={`${
                feedback.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'
              } mt-0.5`}
            ></i>
            <span>{feedback.msg}</span>
          </div>
        )}

        {backup && (
          <>
            {/* Metadatos del archivo */}
            <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-900 truncate">
                  <i className="ri-file-list-3-line mr-1.5 text-primary-600"></i>
                  {fileName}
                </p>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {backup.generatedBy} · {backup.timestamp ? new Date(backup.timestamp).toLocaleString() : 'sin fecha'} ·{' '}
                  {backup.totalRows.toLocaleString()} registros totales
                </p>
              </div>
              <button
                onClick={resetAll}
                className="text-xs px-3 py-2 bg-white border border-background-300 text-foreground-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer flex-shrink-0"
              >
                <i className="ri-refresh-line mr-1"></i>Cambiar archivo
              </button>
            </div>

            {/* Selección de módulos */}
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground-900">¿Qué quieres restaurar?</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={selectAll}
                  className="text-xs text-primary-600 hover:text-primary-700 font-semibold cursor-pointer"
                >
                  Todo
                </button>
                <span className="text-foreground-300">|</span>
                <button
                  onClick={clearAll}
                  className="text-xs text-foreground-500 hover:text-foreground-700 font-semibold cursor-pointer"
                >
                  Nada
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {RESTORE_MODULES.map((mod) => {
                const rows = backup.moduleCounts[mod.id] ?? 0;
                const empty = rows === 0;
                const checked = selected.has(mod.id);
                return (
                  <label
                    key={mod.id}
                    className={`flex items-start gap-3 rounded-lg border px-3 py-3 transition-colors ${
                      empty
                        ? 'border-background-200 bg-background-50 opacity-60 cursor-not-allowed'
                        : checked
                          ? 'border-primary-300 bg-primary-50/60 cursor-pointer'
                          : 'border-background-200 bg-white cursor-pointer hover:bg-background-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={empty}
                      onChange={() => toggleModule(mod.id)}
                      className="mt-0.5 w-4 h-4 accent-primary-500 flex-shrink-0"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-foreground-900 flex items-center gap-1.5">
                          <i className={`${mod.icon} text-primary-600`}></i>
                          {mod.label}
                        </span>
                        <span className="text-xs font-semibold text-foreground-500 whitespace-nowrap">
                          {rows.toLocaleString()}
                        </span>
                      </span>
                      <span className="block text-xs text-foreground-500 mt-0.5 leading-snug">
                        {mod.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1 border-t border-background-100">
              <p className="text-sm text-foreground-600">
                Seleccionados: <strong>{selectedRows.toLocaleString()}</strong> registro(s)
              </p>
              <button
                onClick={() => {
                  setConfirmText('');
                  setConfirmOpen(true);
                }}
                disabled={selectedRows === 0}
                className="text-sm px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap font-medium cursor-pointer"
              >
                <i className="ri-upload-2-line mr-1.5"></i>Restaurar seleccionados
              </button>
            </div>
          </>
        )}
      </div>

      {confirmOpen && backup && (
        <Modal
          title="Restaurar respaldo"
          subtitle="Los registros se fusionan con los datos actuales. No se borra nada."
          onClose={() => !restoring && setConfirmOpen(false)}
          maxWidth="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={() => !restoring && setConfirmOpen(false)}
                disabled={restoring}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmRestore}
                disabled={restoring || confirmText.trim().toUpperCase() !== CONFIRM_WORD}
                className="px-4 py-2 text-sm font-semibold bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {restoring ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Restaurando...
                  </>
                ) : (
                  <>
                    <i className="ri-upload-2-line mr-1.5"></i>Restaurar ahora
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            {restoring ? (
              <div className="space-y-3">
                <p className="text-sm text-foreground-700">
                  Restaurando módulos... {progress.done} de {progress.total}
                  {lastTableLabel ? ` · ${lastTableLabel}` : ''}
                </p>
                <div className="w-full h-2 rounded-full bg-background-100 overflow-hidden">
                  <div
                    className="h-full bg-primary-500 transition-all duration-300"
                    style={{ width: `${progressPct}%` }}
                  ></div>
                </div>
                <p className="text-xs text-foreground-400">
                  No cierres esta ventana mientras se completan las operaciones.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-3 bg-primary-50 border border-primary-200 rounded-lg p-3">
                  <i className="ri-information-line text-primary-600 mt-0.5"></i>
                  <p className="text-sm text-foreground-700">
                    Se restaurarán <strong>{selectedRows.toLocaleString()}</strong> registro(s) en{' '}
                    <strong>{selectedTables.length}</strong> tabla(s). Los datos existentes con el mismo id se
                    actualizarán.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Escribe <strong>{CONFIRM_WORD}</strong> para confirmar
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={CONFIRM_WORD}
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}